import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOrganizacion } from '../context/OrganizacionContext';
import { toast } from 'react-toastify';

const DashboardLider = () => {
  const { lider, empleados, logoutLider } = useAuth();
  const { config, logoUrl } = useOrganizacion();
  const navigate = useNavigate();

  const handleLogout = () => {
    logoutLider();
    toast.success('Sesión cerrada exitosamente');
    navigate('/');
  };

  const handleEvaluarEmpleado = (empleado) => {
    // Preparar datos para la evaluación (líder evaluando a empleado)
    const evaluacionData = {
      evaluador: {
        user_id: lider.id,
        user_name: lider.nombre_completo,
        user_type: 'lider',
        cargo: lider.cargo || 'Líder'
      },
      evaluado: {
        id: empleado.id,
        nombre: empleado.nombre_completo,
        cargo: empleado.cargo
      }
    };
    
    // Guardar en localStorage para que el componente de evaluación lo use
    localStorage.setItem('evaluacionData', JSON.stringify(evaluacionData));
    
    // Navegar a evaluación de empleado (líder → empleado)
    navigate('/evaluacion-empleado-dinamica');
  };

  if (!lider) {
    navigate('/');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              {/* Logo de la organización */}
              {logoUrl && config.mostrar_logo && (
                <img
                  src={logoUrl}
                  alt={config.nombre_organizacion || 'Logo'}
                  className="h-12 w-auto object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <div>
                <h1 
                  className="text-2xl font-bold"
                  style={{ color: config.color_primario }}
                >
                  Dashboard del Líder
                </h1>
                <p className="text-sm text-gray-600">
                  Bienvenido, {lider.nombre_completo}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {/* Botón para cambiar a Dashboard de Empleado (solo si es líder intermedio) */}
              {(() => {
                const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}');
                return userInfo.es_lider_intermedio && (
                  <button
                    onClick={() => navigate('/role-selection')}
                    className="bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-md transition duration-150 ease-in-out flex items-center"
                    title="Cambiar a Dashboard de Empleado"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                    Cambiar Rol
                  </button>
                );
              })()}
              <button
                onClick={handleLogout}
                className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md transition duration-150 ease-in-out"
              >
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Empleados a tu cargo ({empleados.length})
            </h2>
            
            {empleados.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-500">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No hay empleados asignados</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    No tienes empleados a tu cargo en este momento.
                  </p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {empleados.map((empleado) => (
                  <div
                    key={empleado.id}
                    className="bg-white overflow-hidden shadow rounded-lg"
                  >
                    <div className="p-5">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-primary-600 font-medium text-sm">
                              {empleado.nombre_completo.split(' ').map(n => n[0]).join('').substring(0, 2)}
                            </span>
                          </div>
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">
                              {empleado.cargo}
                            </dt>
                            <dd className="text-lg font-medium text-gray-900">
                              {empleado.nombre_completo}
                            </dd>
                          </dl>
                        </div>
                      </div>
                      <div className="mt-5">
                        <div className="text-sm text-gray-500 mb-3">
                          <span className="font-medium">Documento:</span> {empleado.documento_identidad}
                        </div>
                        <button
                          onClick={() => handleEvaluarEmpleado(empleado)}
                          className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-md transition duration-150 ease-in-out"
                        >
                          Realizar Evaluación
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardLider;